import React, { useState, useEffect } from 'react';
import { 
  FiPlus, 
  FiEdit, 
  FiToggleLeft, 
  FiToggleRight,
  FiSave,
  FiX,
  FiCheckCircle,
  FiUserPlus,
  FiUserMinus
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import axiosConfig from '../utils/axiosConfig';
import LoadingSpinner from '../components/LoadingSpinner';
import styles from './Dashboard.module.css';
import modalStyles from './GerenciarUsuarios.module.css';

const GerenciarUsuarios = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [usersWithMedicos, setUsersWithMedicos] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [scopeOptions, setScopeOptions] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMedicoModal, setShowMedicoModal] = useState(false);
  const [selectedMedicoId, setSelectedMedicoId] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar usuários, permissões disponíveis e médicos em paralelo
      const [usersResponse, permissionsResponse, medicosResponse, usersWithMedicosResponse] = await Promise.all([
        axiosConfig.get('/admin/users'),
        axiosConfig.get('/admin/permissions/available'),
        axiosConfig.get('/medical/medicos'),
        axiosConfig.get('/users/com-medicos/listar')
      ]);

      if (usersResponse.data.success) {
        setUsers(usersResponse.data.data.users);
      }

      if (permissionsResponse.data.success) {
        setAvailablePermissions(permissionsResponse.data.data.available_permissions);
        setScopeOptions(permissionsResponse.data.data.scope_options);
      }

      if (medicosResponse.data.success) {
        // A API retorna { success: true, data: { medicos: [...], pagination: {...} } }
        const medicosData = medicosResponse.data.data.medicos || [];
        setMedicos(medicosData);
        console.log('Médicos carregados:', medicosData.length);
      }

      if (usersWithMedicosResponse.data.success) {
        setUsersWithMedicos(usersWithMedicosResponse.data.data.usuarios);
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados dos usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = async (user) => {
    try {
      setLoading(true);
      setSelectedUser(user);
      
      const response = await axiosConfig.get(`/admin/users/${user.id}/permissions`);
      
      if (response.data.success) {
        setUserPermissions(response.data.data.permissions);
        setShowPermissionModal(true);
      }
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      toast.error('Erro ao carregar permissões do usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    try {
      setLoading(true);
      
      // Converter objeto de permissões para array
      const permissionsArray = [];
      
      Object.keys(availablePermissions).forEach(module => {
        Object.keys(availablePermissions[module]).forEach(permission => {
          const scope = userPermissions[module]?.[permission] || 'none';
          
          if (scope !== 'none') {
            permissionsArray.push({
              module,
              permission,
              scope
            });
          }
        });
      });
      
      const response = await axiosConfig.put(`/admin/users/${selectedUser.id}/permissions`, {
        permissions: permissionsArray
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowPermissionModal(false);
        loadData(); // Recarregar dados
      }
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      toast.error('Erro ao salvar permissões');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (module, permission, scope) => {
    setUserPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permission]: scope
      }
    }));
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      
      const response = await axiosConfig.post('/admin/users', newUser);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowCreateModal(false);
        setNewUser({
          name: '',
          email: '',
          password: '',
          role: 'user'
        });
        loadData();
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      const newStatus = !user.ativo;
      
      const response = await axiosConfig.put(`/admin/users/${user.id}/status`, {
        active: newStatus
      });

      if (response.data.success) {
        toast.success(response.data.message);
        loadData();
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const getUserMedicoInfo = (userId) => {
    const userWithMedico = usersWithMedicos.find(u => u.usuario.id === userId);
    return userWithMedico?.medico || null;
  };

  const handleOpenMedicoModal = (user) => {
    const medicoInfo = getUserMedicoInfo(user.id);
    setSelectedUser(user);
    setSelectedMedicoId(medicoInfo?.id || '');
    setShowMedicoModal(true);
  };

  const handleAssociarMedico = async () => {
    if (!selectedMedicoId) {
      toast.error('Selecione um médico');
      return;
    }

    try {
      setLoading(true);
      
      const response = await axiosConfig.put(
        `/users/${selectedUser.id}/associar-medico`,
        { medico_id: selectedMedicoId }
      );

      if (response.data.success) {
        toast.success('Médico associado com sucesso!');
        setShowMedicoModal(false);
        loadData();
      }
    } catch (error) {
      console.error('Erro ao associar médico:', error);
      toast.error(error.response?.data?.message || 'Erro ao associar médico');
    } finally {
      setLoading(false);
    }
  };

  const handleDesassociarMedico = async (user) => {
    if (!window.confirm(`Deseja remover a associação do médico do usuário ${user.name}?`)) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await axiosConfig.delete(`/users/${user.id}/desassociar-medico`);

      if (response.data.success) {
        toast.success('Médico desassociado com sucesso!');
        loadData();
      }
    } catch (error) {
      console.error('Erro ao desassociar médico:', error);
      toast.error(error.response?.data?.message || 'Erro ao desassociar médico');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMedico = (user) => {
    const medicoInfo = getUserMedicoInfo(user.id);
    
    if (medicoInfo) {
      // Se já tem médico, desassocia
      handleDesassociarMedico(user);
    } else {
      // Se não tem médico, abre modal para associar
      handleOpenMedicoModal(user);
    }
  };

  if (loading && users.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Gerenciar Usuários</h1>
        <p>Controle de usuários e permissões do sistema</p>
      </div>

      <div className={styles.actions}>
        <button 
          className={styles.primaryButton}
          onClick={() => setShowCreateModal(true)}
        >
          <FiPlus />
          Novo Usuário
        </button>
      </div>

      <div className={modalStyles.tableContainer}>
        <div className={modalStyles.tableHeader}>
          <h3>Usuários do Sistema ({users.length})</h3>
        </div>
        
        <table className={modalStyles.table}>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Tipo</th>
              <th>Médico</th>
              <th>Permissões</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
              <tbody>
                {users.map(user => {
                  const medicoInfo = getUserMedicoInfo(user.id);
                  
                  return (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.name}</strong>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`${modalStyles.badge} ${user.role === 'admin' ? modalStyles.badgeAdmin : modalStyles.badgeUser}`}>
                        {user.role === 'admin' ? 'Administrador' : 'Usuário'}
                      </span>
                    </td>
                    <td>
                      {medicoInfo ? (
                        <span className={modalStyles.medicoInfo} title={`CRM: ${medicoInfo.crm}`}>
                          {medicoInfo.nome}
                        </span>
                      ) : (
                        <span className={modalStyles.noMedico}>Sem médico</span>
                      )}
                    </td>
                    <td>
                      <span className={modalStyles.permissionCount}>
                        {user.total_permissions} permissão(ões)
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleToggleUserStatus(user)}
                        className={`${modalStyles.statusButton} ${user.active ? modalStyles.statusActive : modalStyles.statusInactive}`}
                      >
                        {user.active ? <FiToggleRight /> : <FiToggleLeft />}
                        {user.active ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                    <td>
                      <div className={modalStyles.actions}>
                        <button
                          onClick={() => handleToggleMedico(user)}
                          className={`${modalStyles.medicoButton} ${medicoInfo ? modalStyles.medicoActive : modalStyles.medicoInactive}`}
                          title={medicoInfo ? 'Remover associação com médico' : 'Associar médico'}
                        >
                          {medicoInfo ? <FiUserMinus /> : <FiUserPlus />}
                          {medicoInfo ? 'Remover Médico' : 'Associar Médico'}
                        </button>
                        <button
                          onClick={() => handleEditPermissions(user)}
                          className={modalStyles.editButton}
                          title="Editar permissões"
                        >
                          <FiEdit />
                          Permissões
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
        </table>
      </div>

      {/* Modal de Permissões */}
      {showPermissionModal && selectedUser && (
        <div className={modalStyles.modal}>
          <div className={modalStyles.modalContent}>
            <div className={modalStyles.modalHeader}>
              <h3>Permissões - {selectedUser.name}</h3>
              <button 
                onClick={() => setShowPermissionModal(false)}
                className={modalStyles.closeButton}
              >
                <FiX />
              </button>
            </div>
            
            <div className={modalStyles.modalBody}>
              {Object.keys(availablePermissions).map(module => (
                <div key={module} className={modalStyles.moduleSection}>
                  <h4 className={modalStyles.moduleTitle}>{module.charAt(0).toUpperCase() + module.slice(1)}</h4>
                  
                  <div className={modalStyles.permissionsGrid}>
                    {Object.keys(availablePermissions[module]).map(permission => (
                      <div key={permission} className={modalStyles.permissionRow}>
                        <div className={modalStyles.permissionInfo}>
                          <strong>{availablePermissions[module][permission]}</strong>
                          <small>{module}.{permission}</small>
                        </div>
                        
                        <select
                          value={userPermissions[module]?.[permission] || 'none'}
                          onChange={(e) => handlePermissionChange(module, permission, e.target.value)}
                          className={modalStyles.scopeSelect}
                        >
                          {Object.keys(scopeOptions).map(scope => (
                            <option key={scope} value={scope}>
                              {scopeOptions[scope]}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className={modalStyles.modalFooter}>
              <button 
                onClick={() => setShowPermissionModal(false)}
                className={modalStyles.cancelButton}
              >
                <FiX />
                Cancelar
              </button>
              <button 
                onClick={handleSavePermissions}
                className={modalStyles.saveButton}
                disabled={loading}
              >
                <FiSave />
                {loading ? 'Salvando...' : 'Salvar Permissões'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação de Usuário */}
      {showCreateModal && (
        <div className={modalStyles.modal}>
          <div className={modalStyles.modalContent}>
            <div className={modalStyles.modalHeader}>
              <h3>Novo Usuário</h3>
              <button 
                onClick={() => setShowCreateModal(false)}
                className={modalStyles.closeButton}
              >
                <FiX />
              </button>
            </div>
            
            <form className="form">
              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">Nome Completo</label>
                    <input
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser(prev => ({...prev, name: e.target.value}))}
                      className="formInput"
                      placeholder="Digite o nome completo"
                      required
                    />
                  </div>
                </div>
                
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">Email</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser(prev => ({...prev, email: e.target.value}))}
                      className="formInput"
                      placeholder="Digite o email"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">Senha</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser(prev => ({...prev, password: e.target.value}))}
                      className="formInput"
                      placeholder="Digite a senha"
                      required
                      minLength="6"
                    />
                  </div>
                </div>
                
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Tipo de Usuário</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser(prev => ({...prev, role: e.target.value}))}
                      className="formSelect"
                    >
                      <option value="user">Usuário</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                </div>
              </div>
            </form>
            
            <div className={modalStyles.modalFooter}>
              <button 
                onClick={() => setShowCreateModal(false)}
                className={modalStyles.secondaryButton}
              >
                Cancelar
              </button>
              <button 
                onClick={handleCreateUser}
                className={modalStyles.primaryButton}
                disabled={loading || !newUser.name || !newUser.email || !newUser.password}
              >
                <FiCheckCircle />
                {loading ? 'Criando...' : 'Criar Usuário'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Associação de Médico */}
      {showMedicoModal && selectedUser && (
        <div className={modalStyles.modal}>
          <div className={modalStyles.modalContent}>
            <div className={modalStyles.modalHeader}>
              <h3>Associar Médico - {selectedUser.name}</h3>
              <button 
                onClick={() => setShowMedicoModal(false)}
                className={modalStyles.closeButton}
              >
                <FiX />
              </button>
            </div>

            <div className={modalStyles.modalBody}>
              <p className={modalStyles.modalDescription}>
                Selecione o médico que será associado a este usuário:
              </p>
              
              <div className="formGroup">
                <label className="formLabel">Médico</label>
                <select
                  value={selectedMedicoId}
                  onChange={(e) => setSelectedMedicoId(e.target.value)}
                  className="formSelect"
                  style={{ width: '100%', padding: '10px', fontSize: '14px' }}
                >
                  <option value="">Selecione um médico...</option>
                  {Array.isArray(medicos) && medicos
                    .filter(m => m.ativo)
                    .map(medico => (
                      <option key={medico.id} value={medico.id}>
                        {medico.nome} - CRM: {medico.crm} - {medico.especialidade}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className={modalStyles.modalFooter}>
              <button 
                onClick={() => setShowMedicoModal(false)}
                className={modalStyles.secondaryButton}
              >
                Cancelar
              </button>
              <button 
                onClick={handleAssociarMedico}
                className={modalStyles.primaryButton}
                disabled={loading || !selectedMedicoId}
              >
                <FiUserPlus />
                {loading ? 'Associando...' : 'Associar Médico'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciarUsuarios;